<h2 class="bd-title">Board</h2>
<alert type="info">
  <coso-alert-message>
    SharePoint Structure Creator is a Dynamics 365 plug-in which automatically creates document libraries or folders in SharePoint based on defined rules to avoid reaching unique permission scope
    limitations when you click on <strong>Documents</strong> button instead of the out-of-the-box integration. For more information, please refer to
    <a class="underline-link" href="https://www.connecting-software.com/sharepoint-structure-creator/" target="_blank">our website</a> or consult the @if (!isSaaS) {
    <a class="underline-link" href="/assets/sharepoint-structure-creator-user-manual.pdf" target="_blank">user manual (PDF 936 KB).</a>
    } @if (isSaaS) {
    <a class="underline-link" [href]="uiAppSettings.sscUserManualUrl" target="_blank">user manual.</a>
    }
  </coso-alert-message>
</alert>
@if (license && license.isValid === false) {
<alert type="warning">
  <coso-alert-message>
    Your license does not include this feature. Please contact us to add this feature to your license, or ask for a trial license if you want to try out this feature.
  </coso-alert-message>
</alert>
} @if (errorMessage) {
<alert type="danger" [dismissible]="true" (onClose)="errorMessage = undefined">
  <coso-alert-message>
    <div class="d-inline" [innerHTML]="errorMessage"></div>
  </coso-alert-message>
</alert>
}

<div class="btn-group-sm my-2">
  @if ((status$ | async) < 4) {
  <button
    type="button"
    class="btn btn-primary"
    data-toggle="tooltip"
    data-placement="top"
    data-original-title="Disables the configuration at Dynamics 365"
    (click)="deactivate()"
    [disabled]="isReadOnly$ | async"
  >
    <span class="icon-stack mr-1" aria-hidden="true">
      <i class="fa fa-fw fa-folder-open" aria-hidden="true"></i>
      <i class="fa fa-fw fa-minus-circle icon-stack-smaller-primary" aria-hidden="true"></i>
    </span>
    Disable
  </button>
  } @if ((status$ | async) >= 4) {
  <button
    type="button"
    class="btn btn-primary"
    data-toggle="tooltip"
    data-placement="top"
    data-original-title="Enables the configuration at Dynamics 365"
    (click)="activate()"
    [disabled]="
      (status$ | async) === ServiceStatusEnum.OnDifferentConfiguration ||
      (status$ | async) === ServiceStatusEnum.OffNoLicense ||
      (status$ | async) === ServiceStatusEnum.OffExpiredLicense ||
      (isReadOnly$ | async)
    "
  >
    <span class="icon-stack mr-1" aria-hidden="true">
      <i class="fa fa-fw fa-folder-open" aria-hidden="true"></i>
      <i class="fa fa-fw fa-check-circle icon-stack-smaller-primary" aria-hidden="true"></i>
    </span>
    Enable
  </button>
  } @if (isSaaS) {
  <button
    type="button"
    class="btn btn-dark ml-2"
    data-toggle="tooltip"
    data-placement="top"
    data-original-title="Activate a token"
    data-toggle="modal"
    data-target="#activateTokenModal"
    [attr.data-token]="token"
    [attr.data-id]="instanceId"
    [attr.data-workertype]="10"
    data-controlleraction="/api/Token/Activate"
    [disabled]="isReadOnly$ | async"
  >
    <span class="fa fa-credit-card-alt mr-1" aria-hidden="true"></span>Activate a Token
  </button>
  }
</div>

<div class="grid-2x">
  <div class="card card-box flex-row mt-2 grid-item bg-transparent border-light">
    <div class="card-title d-flex align-items-center justify-content-around flex-column bg-light p-2">
      <span class="fa fa-fw fa-info card-icon" aria-hidden="true"></span>
      <h3 class="text-center h6">
        Details
        <a
          [href]="isSaaS ? uiAppSettings.sscUserManualUrl : '/assets/sharepoint-structure-creator-user-manual.pdf'"
          target="_blank"
          data-toggle="tooltip"
          data-placement="top"
          data-original-title="Learn more about SharePoint Structure Creator"
          aria-label="Open SharePoint Structure Creator user manual (PDF 933 KB) in another tab"
        >
          <i class="fa fa-question-circle" aria-hidden="true"></i>
        </a>
      </h3>
    </div>
    <div class="card-body d-flex align-items-center">
      <span>Status:</span>
      @switch ((status$ | async)) { @default {
      <span class="badge badge-dark ml-2">-</span>
      } @case (ServiceStatusEnum.On) {
      <span class="badge badge-success ml-2">Active</span>
      } @case (ServiceStatusEnum.OnNoLicense) {
      <span class="badge badge-warning ml-2">Active - no {{ isSaaS ? 'subscription' : 'license' }}</span>
      } @case (ServiceStatusEnum.OnExpiredLicense) {
      <span class="badge badge-warning ml-2">Active - expired {{ isSaaS ? 'subscription' : 'license' }}</span>
      } @case (ServiceStatusEnum.OnBroken) {
      <span class="badge badge-warning ml-2">Active - broken (disable / enable or contact support)</span>
      } @case (ServiceStatusEnum.OnDifferentConfiguration) {
      <span class="badge badge-warning ml-2">Active - different configuration</span>
      } @case (ServiceStatusEnum.Off) {
      <span class="badge badge-danger ml-2">Inactive</span>
      } @case (ServiceStatusEnum.OffNoLicense) {
      <span class="badge badge-danger ml-2">Inactive - no {{ isSaaS ? 'subscription' : 'license' }}</span>
      } @case (ServiceStatusEnum.OffExpiredLicense) {
      <span class="badge badge-danger ml-2">Inactive - expired {{ isSaaS ? 'subscription' : 'license' }}</span>
      } }
    </div>
  </div>

  @if (license) {
  <div class="card card-box flex-row mt-2 grid-item bg-transparent border-light">
    <div class="card-title d-flex align-items-center justify-content-around flex-column bg-light p-2">
      <span class="fa fa-fw fa-tags card-icon" aria-hidden="true"></span>
      <h3 class="text-center h6">{{ isSaaS ? 'Subscription' : 'License' }}</h3>
    </div>
    <div class="card-body d-flex align-items-center">
      @if (license.isAllowed) { @if (license.isValid) {
      <span>
        Your {{ isSaaS ? 'subscription' : 'license' }} is valid until: <strong class="text-uppercase">{{ license.validity | date: 'MMM d, y' }}</strong>
        @if (license.isValidityInherited) {
        <span> (inherited from <a class="underline-link" routerLink="/license">Global License</a>)</span>
        }
      </span>
      } @else {
      <span class="text-danger">
        Your {{ isSaaS ? 'subscription' : 'license' }} has been valid until: <strong class="text-uppercase">{{ license.validity | date: 'MMM d, y' }}</strong>
      </span>
      } } @else {
      <span class="text-danger">No {{ isSaaS ? 'subscription' : 'license' }}</span>
      }
    </div>
  </div>
  }
</div>

@if (isSaaS) {
<coso-token-activation></coso-token-activation>
}
